import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircle, Email, Person, Phone, School } from '@mui/icons-material';
import type { ChildRegistrationRequest } from '../services/childManagementService';
import {
  scottishSchoolService,
  type ScottishCouncil,
  type ScottishSchool,
  type ScottishSchoolWithCouncil,
} from '../services/scottishSchoolService';
import UnknownCouncilSchoolPicker from './registration/UnknownCouncilSchoolPicker';

interface Props {
  onRegister: (data: ChildRegistrationRequest) => Promise<void>;
  onCancel?: () => void;
  title?: string;
  submitButtonText?: string;
  showStepper?: boolean;
  parentId?: number;
  mode?: 'register' | 'link';
}

const STEPS = ['Personal Information', 'School Details', 'Review & Submit'];

const COUNTRIES = [{ id: 'scotland', name: 'Scotland' }];

const UNKNOWN_COUNCIL = '__UNKNOWN_COUNCIL__';
const SCHOOL_NOT_LISTED = '__SCHOOL_NOT_LISTED__';

const SCHOOL_TYPES = [
  { value: 'PRIMARY', label: 'Primary School' },
  { value: 'SECONDARY', label: 'Secondary School' },
  { value: 'PRIVATE', label: 'Private School', disabled: true },
];

const GRADE_YEARS: Record<string, string[]> = {
  PRIMARY: ['P1', 'P2', 'P3', 'P4', 'P5', 'P6', 'P7'],
  SECONDARY: ['S1', 'S2', 'S3', 'S4', 'S5', 'S6'],
};

interface SchoolForm {
  country: string;
  councilId: string;
  councilName: string;
  schoolType: string;
  gradeYear: string;
  schoolId: string;
  schoolName: string;
  unknownCouncil: boolean;
  unknownCouncilName: string;
  unknownSchool: boolean;
}

const EMPTY_SCHOOL: SchoolForm = {
  country: '',
  councilId: '',
  councilName: '',
  schoolType: '',
  gradeYear: '',
  schoolId: '',
  schoolName: '',
  unknownCouncil: false,
  unknownCouncilName: '',
  unknownSchool: false,
};

const ChildRegistrationForm: React.FC<Props> = ({
  onRegister,
  onCancel,
  title = 'Register Child',
  submitButtonText = 'Register Child',
  showStepper = false,
  parentId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 — personal info
  const [personal, setPersonal] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthYear: '',
  });
  const [personalErrors, setPersonalErrors] = useState<Partial<typeof personal>>({});

  // Step 2 — school
  const [school, setSchool] = useState<SchoolForm>(EMPTY_SCHOOL);
  const [schoolErrors, setSchoolErrors] = useState<Partial<SchoolForm>>({});

  // Cascade data
  const [councils, setCouncils] = useState<ScottishCouncil[]>([]);
  const [schools, setSchools] = useState<ScottishSchool[]>([]);
  const [loadingCouncils, setLoadingCouncils] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);

  // Load councils when Scotland is selected
  useEffect(() => {
    if (school.country !== 'scotland') {
      setCouncils([]);
      setSchool(prev => ({ ...prev, councilId: '', councilName: '', schoolType: '', schoolId: '', schoolName: '' }));
      return;
    }
    setLoadingCouncils(true);
    scottishSchoolService.getCouncils()
      .then(setCouncils)
      .catch(() => setCouncils([]))
      .finally(() => setLoadingCouncils(false));
  }, [school.country]);

  // Load schools when council + school type are both set
  useEffect(() => {
    if (!school.councilId || !school.schoolType) {
      setSchools([]);
      setSchool(prev => ({ ...prev, schoolId: '', schoolName: '' }));
      return;
    }
    setLoadingSchools(true);
    scottishSchoolService.getSchools(Number(school.councilId), school.schoolType)
      .then(setSchools)
      .catch(() => setSchools([]))
      .finally(() => setLoadingSchools(false));
  }, [school.councilId, school.schoolType]);

  const setSchoolField = <K extends keyof SchoolForm>(key: K, value: string) => {
    setSchool(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'country') {
        next.councilId = ''; next.councilName = '';
        next.schoolType = ''; next.schoolId = ''; next.schoolName = '';
        next.unknownCouncil = false; next.unknownCouncilName = ''; next.unknownSchool = false;
      }
      if (key === 'councilId') {
        if (value === UNKNOWN_COUNCIL) {
          next.unknownCouncil = true;
          next.councilName = '';
          next.schoolType = ''; next.schoolId = ''; next.schoolName = '';
          next.unknownSchool = false;
        } else {
          next.unknownCouncil = false;
          const c = councils.find(c => String(c.id) === value);
          next.councilName = c?.name ?? '';
          next.schoolType = ''; next.schoolId = ''; next.schoolName = '';
          next.unknownSchool = false;
        }
      }
      if (key === 'schoolType') {
        next.gradeYear = ''; next.schoolId = ''; next.schoolName = ''; next.unknownSchool = false;
      }
      if (key === 'schoolId') {
        if (value === SCHOOL_NOT_LISTED) {
          next.unknownSchool = true;
          next.schoolName = '';
        } else {
          next.unknownSchool = false;
          const s = schools.find(s => String(s.id) === value);
          next.schoolName = s?.name ?? '';
        }
      }
      return next;
    });
    setSchoolErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const validatePersonal = (): boolean => {
    const errs: Partial<typeof personal> = {};
    if (!personal.firstName.trim()) errs.firstName = 'First name is required';
    if (!personal.lastName.trim()) errs.lastName = 'Last name is required';
    if (!personal.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(personal.email)) errs.email = 'Email is invalid';
    setPersonalErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateSchool = (): boolean => {
    const errs: Partial<SchoolForm> = {};
    if (!school.country) errs.country = 'Country is required';
    if (!school.councilId && !school.unknownCouncil) errs.councilId = 'Council is required';
    if (school.unknownCouncil && !school.unknownCouncilName.trim()) errs.councilName = 'Please enter your council name';
    if (!school.schoolType && !school.unknownCouncil) errs.schoolType = 'School type is required';
    if (school.schoolType && !school.gradeYear) errs.gradeYear = 'Year group is required';
    if (!school.schoolId && !school.unknownSchool && !school.unknownCouncil) errs.schoolId = 'School is required';
    if ((school.unknownSchool || school.unknownCouncil) && !school.schoolName.trim()) errs.schoolName = 'Please enter your school name';
    setSchoolErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validatePersonal()) setActiveStep(1);
    else if (activeStep === 1 && validateSchool()) setActiveStep(2);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    if (!validatePersonal() || !validateSchool()) {
      setActiveStep(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: ChildRegistrationRequest = {
        firstName: personal.firstName,
        lastName: personal.lastName,
        email: personal.email,
        password: '',
        dateOfBirth: '',
        phoneNumber: personal.phoneNumber || undefined,
        birthYear: personal.birthYear ? parseInt(personal.birthYear, 10) : undefined,
        schoolName: school.schoolName,
        gradeLevel: school.gradeYear,
        parentUserId: parentId ?? 0,
        councilId: school.councilId && school.councilId !== UNKNOWN_COUNCIL ? Number(school.councilId) : undefined,
        councilName: school.councilName || undefined,
        unknownSchool: school.unknownSchool || undefined,
        unknownCouncilName: school.unknownCouncilName || undefined,
      };
      await onRegister(payload);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to register child');
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Person /> Personal Information
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="First Name"
            value={personal.firstName}
            onChange={e => { setPersonal(p => ({ ...p, firstName: e.target.value })); setPersonalErrors(p => ({ ...p, firstName: undefined })); }}
            error={!!personalErrors.firstName}
            helperText={personalErrors.firstName}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Last Name"
            value={personal.lastName}
            onChange={e => { setPersonal(p => ({ ...p, lastName: e.target.value })); setPersonalErrors(p => ({ ...p, lastName: undefined })); }}
            error={!!personalErrors.lastName}
            helperText={personalErrors.lastName}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={personal.email}
            onChange={e => { setPersonal(p => ({ ...p, email: e.target.value })); setPersonalErrors(p => ({ ...p, email: undefined })); }}
            error={!!personalErrors.email}
            helperText={personalErrors.email}
            InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Phone Number (optional)"
            value={personal.phoneNumber}
            onChange={e => setPersonal(p => ({ ...p, phoneNumber: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start"><Phone /></InputAdornment> }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Year of Birth (optional)"
            type="number"
            value={personal.birthYear}
            onChange={e => setPersonal(p => ({ ...p, birthYear: e.target.value }))}
            helperText="Used for credential age-gating"
            inputProps={{ min: 1980, max: new Date().getFullYear() }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderSchoolStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <School /> School Details
      </Typography>
      <Grid container spacing={3}>

        {/* Country */}
        <Grid size={12}>
          <FormControl fullWidth error={!!schoolErrors.country}>
            <InputLabel>Country</InputLabel>
            <Select value={school.country} onChange={e => setSchoolField('country', e.target.value)} label="Country">
              {COUNTRIES.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
            {schoolErrors.country && <FormHelperText>{schoolErrors.country}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Council — Scotland only */}
        {school.country === 'scotland' && (
          <Grid size={12}>
            <FormControl fullWidth error={!!schoolErrors.councilId} disabled={loadingCouncils}>
              <InputLabel>
                {loadingCouncils ? 'Loading councils…' : 'Council'}
              </InputLabel>
              <Select value={school.councilId} onChange={e => setSchoolField('councilId', e.target.value)} label="Council">
                {councils.map(c => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                ))}
                <Divider />
                <MenuItem value={UNKNOWN_COUNCIL} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  My council isn't listed
                </MenuItem>
              </Select>
              {schoolErrors.councilId && <FormHelperText>{schoolErrors.councilId}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        {/* Unknown council picker */}
        {school.unknownCouncil && (
          <Grid size={12}>
            <UnknownCouncilSchoolPicker
              onSchoolSelected={(s: ScottishSchoolWithCouncil) => {
                setSchool(prev => ({
                  ...prev,
                  councilId: String(s.councilId),
                  councilName: s.councilName,
                  schoolId: String(s.id),
                  schoolName: s.name,
                  schoolType: s.schoolType ?? '',
                  unknownCouncil: false,
                  unknownCouncilName: '',
                  unknownSchool: false,
                }));
              }}
              onSchoolNotFound={(councilName: string, schoolType: string, schoolName: string) => {
                setSchool(prev => ({
                  ...prev,
                  unknownCouncilName: councilName,
                  schoolType,
                  schoolName,
                  unknownSchool: false,
                }));
              }}
            />
            {schoolErrors.councilName && <FormHelperText error>{schoolErrors.councilName}</FormHelperText>}
            {schoolErrors.schoolName && <FormHelperText error>{schoolErrors.schoolName}</FormHelperText>}
          </Grid>
        )}

        {/* School type */}
        {school.councilId && (
          <Grid size={12}>
            <FormControl fullWidth error={!!schoolErrors.schoolType}>
              <InputLabel>School Type</InputLabel>
              <Select value={school.schoolType} onChange={e => setSchoolField('schoolType', e.target.value)} label="School Type">
                {SCHOOL_TYPES.map(t => (
                  <MenuItem key={t.value} value={t.value} disabled={t.disabled}>
                    {t.label}{t.disabled ? ' — coming soon' : ''}
                  </MenuItem>
                ))}
              </Select>
              {schoolErrors.schoolType && <FormHelperText>{schoolErrors.schoolType}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        {/* Year group */}
        {school.schoolType && (
          <Grid size={12}>
            <FormControl fullWidth error={!!schoolErrors.gradeYear}>
              <InputLabel>Year Group</InputLabel>
              <Select value={school.gradeYear} onChange={e => setSchoolField('gradeYear', e.target.value)} label="Year Group">
                {(GRADE_YEARS[school.schoolType] ?? []).map(yr => (
                  <MenuItem key={yr} value={yr}>{yr}</MenuItem>
                ))}
              </Select>
              {schoolErrors.gradeYear && <FormHelperText>{schoolErrors.gradeYear}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        {/* School list */}
        {school.councilId && school.councilId !== UNKNOWN_COUNCIL && school.schoolType && (
          <Grid size={12}>
            <FormControl fullWidth error={!!schoolErrors.schoolId} disabled={loadingSchools}>
              <InputLabel>
                {loadingSchools ? 'Loading schools…' : schools.length === 0 ? 'No schools found' : 'School'}
              </InputLabel>
              <Select value={school.schoolId} onChange={e => setSchoolField('schoolId', e.target.value)} label="School">
                {schools.map(s => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                ))}
                <Divider />
                <MenuItem value={SCHOOL_NOT_LISTED} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                  My school isn't in this list
                </MenuItem>
              </Select>
              {schoolErrors.schoolId && <FormHelperText>{schoolErrors.schoolId}</FormHelperText>}
              {!loadingSchools && schools.length === 0 && (
                <FormHelperText>No schools found for the selected council and type</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Free-text school name when school not listed */}
        {school.unknownSchool && (
          <Grid size={12}>
            <TextField
              fullWidth
              required
              label="School name"
              value={school.schoolName}
              onChange={e => setSchool(prev => ({ ...prev, schoolName: e.target.value }))}
              error={!!schoolErrors.schoolName}
              helperText={schoolErrors.schoolName || 'Enter your school name — an admin will confirm the details'}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  const ReviewRow = ({ label, value }: { label: string; value: string }) => (
    <>
      <Grid size={{ xs: 12, sm: 4 }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <Typography variant="body1" fontWeight={500}>{value || '—'}</Typography>
      </Grid>
    </>
  );

  const renderReviewStep = () => (
    <Box>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle /> Review & Submit
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>Personal Information</Typography>
          <Grid container spacing={1} sx={{ mb: 3 }}>
            <ReviewRow label="Name" value={`${personal.firstName} ${personal.lastName}`} />
            <ReviewRow label="Email" value={personal.email} />
            <ReviewRow label="Phone" value={personal.phoneNumber || 'Not provided'} />
            <ReviewRow label="Year of Birth" value={personal.birthYear || 'Not provided'} />
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" sx={{ mb: 2 }}>School Details</Typography>
          <Grid container spacing={1}>
            <ReviewRow label="Country" value={COUNTRIES.find(c => c.id === school.country)?.name ?? school.country} />
            <ReviewRow label="Council" value={school.councilName} />
            <ReviewRow label="School Type" value={SCHOOL_TYPES.find(t => t.value === school.schoolType)?.label ?? school.schoolType} />
            <ReviewRow label="Year Group" value={school.gradeYear} />
            <ReviewRow label="School" value={school.schoolName} />
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  const stepContent = [renderPersonalStep, renderSchoolStep, renderReviewStep];

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>{title}</Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {showStepper && (
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
          {STEPS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
      )}

      <Box sx={{ mb: 3 }}>{stepContent[activeStep]()}</Box>

      <Divider sx={{ my: 3 }} />

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Box>
          {showStepper && activeStep > 0 && (
            <Button onClick={handleBack} disabled={loading}>Back</Button>
          )}
          {onCancel && <Button onClick={onCancel} disabled={loading}>Cancel</Button>}
        </Box>
        <Box>
          {showStepper ? (
            activeStep === STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {submitButtonText}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>Next</Button>
            )
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
            >
              {submitButtonText}
            </Button>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default ChildRegistrationForm;
